import type FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { getMapView } from "./arcgisUtils";
import { metersPerNauticalMile } from "./constants";
import { uuidv4 } from "./helpers";
import { DefaultSectorValues, Sector, SectorColor } from "./lighthouseConstants";
import { NFSStatus } from "./nfsUtils";

/** A sector still carrying its end angle, needed only while dark sectors are reconstructed. */
export type SectorWithEndAngle = Sector & { endAngle: number };

/** The Lyssektor table columns this module reads. Lengths and radius are nautical miles. */
export interface SectorRow {
    objectid: number;
    globalid: string;
    sektor_nr: number;
    lys_farge: number;
    retning_sektorlinje_1: number;
    retning_sektorlinje_2: number;
    sektor_radius: number;
    lengde_sektorlinje_1: number;
    lengde_sektorlinje_2: number;
    beskrivelse: string | null;
    nfs_status: number;
    nfs_status_line1: number;
    nfs_status_line2: number;
}

export type SectorReadResult =
    | { kind: "sectors"; lighthouseId: string; sectorObjectId: number; sectors: Sector[] }
    /** The layer has no Lyssektor relationship, or nothing at all is related to it. */
    | { kind: "no-related-table" }
    /** Sectors exist on the relationship, but none belong to this object. */
    | { kind: "not-related-to-object" }
    /** No map view yet. A precondition failure, deliberately not a domain outcome: mapping it
     *  onto "no-related-table" would make a caller save a single light sector for it. */
    | { kind: "no-map-view" };

/**
 * Brings both direction columns into [0, 360), rounded to two decimals.
 *
 * A bearing means nothing beyond a hundredth of a degree, but dragging a sector line computes
 * one from radians, so stored angles carry the full precision of that arithmetic. The sector
 * editor has always displayed two decimals; rounding here means the definition table shows the
 * same thing, a shared boundary reads identically on both of its sides, and a sector nobody
 * touched is not mistaken for an edited one. Rounding runs before the wrap, so an angle that
 * rounds up to 360 comes back as the north it means.
 *
 * North is 0 and 360 is the same bearing, but the
 * dark-sector reconstruction compares the numbers, so a closing boundary stored as 360 does not
 * meet the first sector's 0 and a zero-width dark sector gets fabricated at the seam - showing
 * up as a phantom dark sector in the editor and as an unfixable row in the definition table.
 *
 * Nothing in this codebase writes 360: saveLighthouse takes each end boundary from the next
 * sector's start angle, and the editor folds typed angles into the circle on blur. Rows like
 * that are legacy or imported, which is what this normalisation exists for.
 */
const normaliseAngle = (angle: number) => {
    // Rounding after the wrap, because the wrap's own arithmetic introduces the noise it is
    // meant to remove: ((43.20000000000001 % 360) + 360) % 360 is 43.199999999999996.
    const wrapped = ((angle % 360) + 360) % 360;
    const rounded = Math.round(wrapped * 100) / 100;
    return rounded === 360 ? 0 : rounded;
};

export const normaliseSectorRowAngles = (rows: SectorRow[]): SectorRow[] =>
    rows.map((row) => ({
        ...row,
        retning_sektorlinje_1: normaliseAngle(row.retning_sektorlinje_1),
        retning_sektorlinje_2: normaliseAngle(row.retning_sektorlinje_2),
    }));

/** Maps Lyssektor attribute rows to sectors, converting the stored nautical miles to metres. */
export const sectorRowsToSectors = (rows: SectorRow[], lighthouseId: string): SectorWithEndAngle[] =>
    rows
        .map((sector, index) => {
            return {
                globalid: sector.globalid,
                lighthouseId: lighthouseId,
                color: sector.lys_farge.toString(),
                startAngle: sector.retning_sektorlinje_1,
                endAngle: sector.retning_sektorlinje_2, // We need this temporarily to compute the dark sectors
                innerRadius: sector.sektor_radius * metersPerNauticalMile,
                startLineLength: sector.lengde_sektorlinje_1 * metersPerNauticalMile,
                endLineLength: sector.lengde_sektorlinje_2 * metersPerNauticalMile,
                index: index,
                description: sector.beskrivelse,
                nfs_status: sector.nfs_status,
                nfs_status_line1: sector.nfs_status_line1,
                nfs_status_line2: sector.nfs_status_line2,
            } as SectorWithEndAngle;
        })
        .sort((a, b) => a.startAngle! - b.startAngle!);

/**
 * The Lyssektor table stores only lit sectors. Any arc no lit sector covers is a dark
 * sector, reconstructed here, after which the temporary end angle is dropped.
 */
export const fillDarkSectors = (sectors: SectorWithEndAngle[], lighthouseId: string): Sector[] => {
    const sectorsIncludingDark: SectorWithEndAngle[] = [];

    for (const [index, sector] of sectors.entries()) {
        const nextIndex = (index + 1) % sectors.length;

        sectorsIncludingDark.push(sector);

        // Add a dark sector if the next sector does not start where the current sector ends.
        // The full-circle guard is unreachable for rows that came through normaliseSectorRowAngles,
        // and is kept for callers that hand over raw angles.
        if (
            Math.abs(sectors[nextIndex].startAngle! - sector.endAngle) > 0.001 &&
            !(sector.startAngle === 0 && sector.endAngle === 360)
        ) {
            sectorsIncludingDark.push({
                globalid: uuidv4(),
                lighthouseId: lighthouseId,
                color: SectorColor.DARK,
                startAngle: sector.endAngle,
                endAngle: sectors[nextIndex].startAngle!,
                innerRadius: DefaultSectorValues.defaultInnerRadius,
                startLineLength: sector.endLineLength,
                endLineLength: sectors[nextIndex].startLineLength,
                index: index + 1,
                description: "",
                nfs_status: NFSStatus.UNDEFINED,
                nfs_status_line1: NFSStatus.PROJECTED,
                nfs_status_line2: NFSStatus.PROJECTED,
            });
        }
    }

    sectorsIncludingDark.sort((a, b) => a.startAngle! - b.startAngle!);

    // Update all sector indices
    for (const [index, sector] of sectorsIncludingDark.entries()) {
        sector.index = index;
    }

    // Remove the endAngle attribute from all sectors
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructured only to omit it from `sector`
    return sectorsIncludingDark.map(({ endAngle: _endAngle, ...sector }) => sector);
};

/**
 * Reads the sectors belonging to one object, with no side effects — no saving, no drawing,
 * no selection, no loading flag. `loadLighthouse` uses it for the read half of its work;
 * the replace flow uses it to copy the sectors of the object being replaced.
 */
export const readSectorsForFeature = async (objectid: number, layer: FeatureLayer): Promise<SectorReadResult> => {
    const view = getMapView();
    if (!view?.map) return { kind: "no-map-view" };

    const sectorTable = view.map.findTableById("Lyssektor") as FeatureLayer;

    const relation = layer.relationships?.find((relationship) => relationship.name === "Lyssektor");
    // A replaced object need not be a light at all, in which case there is nothing to read
    if (!relation) return { kind: "no-related-table" };

    const sectorTableData = await layer.queryRelatedFeatures({
        outFields: ["*"],
        relationshipId: relation.id,
        objectIds: [objectid],
    });

    if (Object.keys(sectorTableData).length === 0) return { kind: "no-related-table" };
    if (!sectorTableData[objectid]) return { kind: "not-related-to-object" };

    const lighthouseId = sectorTableData[objectid].features[0].attributes.fknavinst;

    const sectors = await sectorTable.queryFeatures({
        where: `fknavinst = '${lighthouseId}'`,
        outFields: ["*"],
    });

    const sectorAttributes = sectors.features.map((feature) => feature.attributes);
    // The fknavinst query can come back empty even though the relationship produced a lighthouse
    // id, e.g. a stale or dangling reference - nothing to report as related sectors then.
    if (sectorAttributes.length === 0) return { kind: "not-related-to-object" };
    sectorAttributes.sort((a, b) => a.sektor_nr - b.sektor_nr);

    return {
        kind: "sectors",
        lighthouseId,
        sectorObjectId: sectorAttributes[0].objectid,
        sectors: fillDarkSectors(
            sectorRowsToSectors(normaliseSectorRowAngles(sectorAttributes), lighthouseId),
            lighthouseId
        ),
    };
};
